/* eslint-disable tailwindcss/no-custom-classname */
import React, {
  forwardRef, useContext, useState, useEffect, useMemo, Fragment, useRef, useCallback
} from 'react';
import {
  string, func, arrayOf, shape, number
} from 'prop-types';
import {
  QueryContext, QueryProvider, extend,
  bool as boolType,
  params, alias,
  shape as shapeType,
  string as stringType,
  number as numberType,
  arrayOf as arrayOfType,
  customType,
  DataProviderClient
} from '@thd-nucleus/data-sources';
import { ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import { CustomProductPodDetails, ProductPodGroup } from '@thd-olt-component-react/product-pod-group';
import { AppContext } from '@thd-nucleus/app-render';
import { Col } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { gql } from '@apollo/client';
import { publish } from '../../publisher';
import { ResultsWrapped } from '../ResultsWrapped';
import { ProductResultsContext } from '../ProductResultsContext';
import { ScreenSize } from '../../product-results-helpers';
import { useProductsPerRow } from './hooks/useProductsPerRow';
import { useMoreProductsOrchestrator } from './hooks/useMoreProductsOrchestrator';

const ProductResultsGrid = forwardRef(({
  pageType,
  defaultVariables,
  podFunction,
  slots,
  additionalSearchParams,
  keyword,
  storeId,
  initialPlaceholderSize,
  dynamicHydrationPlaceholderSize
}, ref) => {

  const { data, loading, responseLoading, setData } = useContext(ProductResultsContext);
  const { isClientResolved } = useContext(QueryContext);
  const { channel } = useContext(ExperienceContext);
  const { instance: browseInstance } = useContext(AppContext);

  const isSSRWithPlaceholdersEnabled = useConfigService('fs-prop:isSSRWithPlaceholdersEnabled');

  const isSearch = pageType === 'search';
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const { isLocalized } = useStore({ varnish: !disableSSR && !isSearch, online: false });

  const showPlaceholderPodsFlag = !browseInstance?.thdSEOBotDetection
    && (isSSRWithPlaceholdersEnabled || (!isSSRWithPlaceholdersEnabled && !isSearch));
  const [shouldShowInitialPlaceholders, setShouldShowInitialPlaceholders] = useState(showPlaceholderPodsFlag);

  const isDesktop = channel === 'desktop';
  const client = DataProviderClient.getClient();

  const shownPlaceholders = useRef(0);

  const { products = [], additionalProductsIds = {} } = data?.searchModel || {};
  const { productIds = [] } = additionalProductsIds || {};
  const productsFromFirstLoad = useRef(products.length);
  const sponsoredProductsDetails = useMemo(() => {
    return productIds.reduce((acc, product) => {
      const { itemId, isSponsored, sponsoredBeacon, sponsoredMetadata } = product;
      if (isSponsored) {
        acc[itemId] = {
          isSponsored,
          sponsoredBeacon,
          sponsoredMetadata
        };
      }
      return acc;
    }, {});
  }, [productIds]);

  useEffect(() => {
    const showPlaceholders = showPlaceholderPodsFlag ? (loading || responseLoading)
      : (!products?.length && loading);

    if (showPlaceholders !== shouldShowInitialPlaceholders) {
      setShouldShowInitialPlaceholders(showPlaceholders);
    }
  }, [loading, responseLoading]);

  const [productsPerRow, matchesScreen] = useProductsPerRow({ channel });

  const [moreProductsResponse, itemsQueued, renderRequestSyncStatus] = useMoreProductsOrchestrator({
    isContextLoading: (loading || responseLoading),
    additionalSearchParams,
    storeId,
    channel,
    maxProductsPerRequest: dynamicHydrationPlaceholderSize,
    loadedProducts: products,
    productsToLoad: productIds
  });

  const isLoading = loading || responseLoading || moreProductsResponse.loading;

  const componentsToRender = useMemo(() => {
    let screen = matchesScreen.current;

    // assume xl on desktop on ssr and on 2xl
    if ((matchesScreen.current === ScreenSize.DX_LARGE || typeof window === 'undefined')
      && channel === 'desktop') {
      screen = ScreenSize.X_LARGE;
    }

    return slots?.reduce((res, slot) => {
      res[slot.position[screen]] = slot.component;
      return res;
    }, []);
  }, [matchesScreen.current, slots]);

  const mountedFn = ({ mounted }) => {
    if (!isLocalized) {
      return false;
    }
    if (responseLoading) {
      return false;
    }
    return mounted;
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip;
    }
    const isResolved = isClientResolved({ queryName: 'searchModel' });

    if (attributes.product.fulfillment) {
      if (isResolved) {
        return skip;
      }
      return true;
    }
    return skip;
  };

  const writeSponsoredProductToCache = (dataSources, itemId, additionalProduct) => {
    const { isSponsored, sponsoredBeacon, sponsoredMetadata } = additionalProduct;

    client.writeFragment({
      id: `base-${dataSources}-${itemId}`,
      fragment: gql`
        fragment info on BaseProduct {
          info {
            isSponsored,
            sponsoredBeacon {
              onClickBeacon,
              onViewBeacon,
              onClickBeacons,
              onViewBeacons
            },
            sponsoredMetadata {
              campaignId,
              placementId,
              slotId,
              sponsoredId,
              trackSource
            }
          }
        }
      `,
      data: {
        info: {
          isSponsored,
          sponsoredBeacon,
          sponsoredMetadata
        }
      }
    });
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const moreProductsResponseProducts = moreProductsResponse.data?.searchModel?.products || [];
    if (moreProductsResponseProducts.length > 0) {

      // process sponsored products
      moreProductsResponseProducts.forEach((product) => {
        const { itemId, dataSources } = product;
        const additionalProduct = sponsoredProductsDetails[itemId];

        if (additionalProduct?.isSponsored) {
          writeSponsoredProductToCache(dataSources, itemId, additionalProduct);
        }
      });

      // update search model
      if (moreProductsResponse.data?.searchModel?.id) {
        const newProductsToDisplay = [...products, ...moreProductsResponseProducts];
        const newSearchModel = { ...data?.searchModel, products: newProductsToDisplay };
        publish('more-products', newSearchModel);
        setData({ ...data, searchModel: newSearchModel });
      }
    }
  }, [moreProductsResponse.data?.searchModel?.id]);

  shownPlaceholders.current = itemsQueued?.length > 0 ? 0 : shownPlaceholders.current;

  const defaultVariablesFallback = {
    storeId,
    skipKPF: true,
    skipSubscribeAndSave: true,
    skipInstallServices: false
  };

  /**
   * return an array of objects containing
   * start: starting index for pods loop
   * productSetSize: how many products are grouped
   * slotIndex: the index for the component to be rendered in the slot
   */
  const productSets = useMemo(() => {
    if (!componentsToRender?.length) {
      return [];
    }

    const firstLoadCount = productsFromFirstLoad.current;
    const additionalCount = additionalProductsIds?.productIds?.length || 0;
    const totalProducts = firstLoadCount + additionalCount;

    if (totalProducts === 0) {
      return [];
    }

    const sets = [];
    let currentIndex = 0;

    componentsToRender.forEach((component, i) => {
      if (!component || currentIndex >= totalProducts) {
        return;
      }

      const nextPosition = i * productsPerRow;

      if (nextPosition <= currentIndex) {
        return;
      }

      sets.push({
        start: currentIndex,
        productSetSize: Math.min(nextPosition, totalProducts) - currentIndex,
        slotIndex: nextPosition <= totalProducts ? i : -1
      });

      currentIndex = nextPosition;
    });

    while (currentIndex < totalProducts) {
      const remainingInFirstLoadPattern = firstLoadCount - (currentIndex % firstLoadCount);
      const remainingProducts = totalProducts - currentIndex;

      sets.push({
        start: currentIndex,
        productSetSize: Math.min(remainingInFirstLoadPattern, remainingProducts),
        slotIndex: -1
      });

      currentIndex += remainingInFirstLoadPattern;
    }

    return sets;
  }, [productsPerRow, additionalProductsIds, componentsToRender]);

  const renderSlot = useCallback((slotIndex) => {
    return (
      <div className="sui-w-full" data-testid={`slot-${slotIndex}`}>
        {componentsToRender && componentsToRender[slotIndex]}
      </div>
    );
  }, [componentsToRender]);

  const renderPods = useCallback((startIndex, numberOfPods) => {
    let podsToRender = numberOfPods || 0;
    if (products && startIndex + numberOfPods > products.length) {
      podsToRender = products.length - startIndex;
    }
    if (!products || podsToRender <= 0) {
      return null;
    }

    return (
      <ProductPodGroup
        DetailsPod={CustomProductPodDetails}
        keyword={keyword}
      >
        {Array.from({ length: podsToRender }).map((_elem, idx) => {
          const itemIndex = startIndex + idx;
          const itemId = products[itemIndex].identifiers?.itemId;
          return (
            <ErrorBoundary
              id={`plp-product-pod__${itemIndex}`}
              index={itemIndex}
              key={`${itemId}-${itemIndex}`}
              name={`Product Pod: ${itemIndex}`}
            >
              <Col
                className="browse-search__pod"
                fallback={isDesktop ? '6' : '12'}
                lg="3"
                md="3"
                sm="4"
                xs="6"
              >
                {podFunction(products[itemIndex], itemIndex)}
              </Col>
            </ErrorBoundary>
          );
        })}
      </ProductPodGroup>
    );
  }, [products, keyword, isDesktop, podFunction]);

  const renderPlaceholderPods = (startIndex, numberOfPods) => {
    if (!numberOfPods || numberOfPods <= 0) {
      return null;
    }

    const isQueueEmpty = (itemsQueued?.length || 0) === 0;
    if (isQueueEmpty && renderRequestSyncStatus?.inSync) {
      return null;
    }

    const queueLength = itemsQueued?.length || 0;

    // Case 1: No products or products that don't extend to this range
    if (!products || startIndex >= products?.length) {
      const remainingPlaceholders = dynamicHydrationPlaceholderSize - shownPlaceholders.current;

      if (remainingPlaceholders <= 0) {
        return null;
      }

      const calculatedPlaceholders = Math.min(numberOfPods, remainingPlaceholders, queueLength);

      shownPlaceholders.current += calculatedPlaceholders;

      const needsSyncBasedCount = calculatedPlaceholders <= 0 && !renderRequestSyncStatus?.inSync;
      const finalPodCount = needsSyncBasedCount
        ? (renderRequestSyncStatus?.difference || 0)
        : calculatedPlaceholders;

      return finalPodCount > 0 ? <ProductPodPlaceholder podsCount={finalPodCount} /> : null;
    }

    // Case 2: We have some products but need placeholders for the remainder
    const availableProductCount = Math.min(numberOfPods, products.length - startIndex);
    const neededPlaceholders = numberOfPods - Math.max(0, availableProductCount);

    if (neededPlaceholders <= 0) {
      return null;
    }

    // Calculate placeholders for case 2
    const calculatedPlaceholders = Math.min(neededPlaceholders, queueLength);
    shownPlaceholders.current += calculatedPlaceholders;

    // Use sync state for final count determination
    const needsSyncBasedCount = calculatedPlaceholders <= 0 && !renderRequestSyncStatus?.inSync;
    const finalPlaceholderCount = needsSyncBasedCount
      ? (renderRequestSyncStatus?.difference || 0)
      : calculatedPlaceholders;

    return finalPlaceholderCount > 0 ? <ProductPodPlaceholder podsCount={finalPlaceholderCount} /> : null;
  };

  return (
    <ResultsWrapped ref={ref}>
      <div id="plp-grid" className="grid" data-testid="plp-grid">
        {shouldShowInitialPlaceholders && (<ProductPodPlaceholder podsCount={initialPlaceholderSize} />)}

        {!!products?.length && !shouldShowInitialPlaceholders && (
          <QueryProvider
            dataSource="searchNav"
            defaultVariables={defaultVariables || defaultVariablesFallback}
            mounted={mountedFn}
            skip={skipFn}
          >
            {productSets?.map((productSet) => {
              return (
                <Fragment key={`productSet-${productSet.start}-${productSet.productSetSize}`}>
                  { renderPods(productSet.start, productSet.productSetSize) }
                  { renderPlaceholderPods(productSet.start, productSet.productSetSize) }
                  { renderSlot(productSet.slotIndex) }
                </Fragment>
              );
            })}
          </QueryProvider>
        )}
      </div>
    </ResultsWrapped>
  );
});

const createGridWithPod = (CustomPod) => {
  ProductPodGroup.dataModel = extend(ProductPodGroup.dataModel || {},
    CustomPod,
    CustomProductPodDetails);

  const MoreProductModel = {
    moreProducts: alias('searchModel')
      .params({
        storeId: stringType(),
        itemIds: arrayOfType(stringType()),
        isBrandPricingPolicyCompliant: boolType(),
        channel: customType('Channel').enum(['DESKTOP', 'MOBILE', 'TABLET'], 'DESKTOP'),
        storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL'),
        additionalSearchParams: customType('AdditionalParams').shape({
          deliveryZip: stringType(),
          mcvisId: stringType(),
          sponsored: boolType()
        }),
        loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
          svocID: stringType(),
          programTiers: arrayOfType(
            shapeType({
              tier: stringType(),
              program: stringType()
            })
          )
        })
      }).shape({
        id: stringType(),
        products: params().arrayOf(CustomPod.dataModel.product)
      })
  };

  ProductResultsGrid.dataModel = extend({ searchModel: params(
    {
      keyword: stringType(),
      navParam: stringType(),
      channel: customType('Channel').enum(['DESKTOP', 'MOBILE', 'TABLET'], 'DESKTOP'),
      storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL'),
      dynamicHydration: boolType(),
      dynamicHydrationPageSize: numberType()
    }
  ).shape({
    id: stringType(),
    products: params({ startIndex: numberType() }).arrayOf(CustomPod.dataModel.product),
    additionalProductsIds: shapeType({
      productIds: arrayOfType(shapeType({
        itemId: stringType(),
        isSponsored: boolType(),
        sponsoredBeacon: shapeType({
          onClickBeacon: stringType(),
          onViewBeacon: stringType(),
          onClickBeacons: arrayOfType(stringType()),
          onViewBeacons: arrayOfType(stringType())
        }),
        sponsoredMetadata: shapeType({
          campaignId: stringType(),
          placementId: stringType(),
          slotId: stringType(),
          sponsoredId: stringType(),
          trackSource: stringType()
        })
      }))
    })
  }) },
  ProductPodGroup,
  MoreProductModel);

  return ProductResultsGrid;
};

ProductResultsGrid.displayName = 'ProductResultsGrid';
ProductResultsGrid.propTypes = {
  defaultVariables: shape({}),
  additionalSearchParams: shape({}),
  podFunction: func.isRequired,
  pageType: string,
  keyword: string,
  storeId: string,
  slots: arrayOf(shape({})),
  initialPlaceholderSize: number,
  dynamicHydrationPlaceholderSize: number
};

ProductResultsGrid.defaultProps = {
  pageType: null,
  keyword: null,
  defaultVariables: null,
  additionalSearchParams: null,
  slots: null,
  storeId: null,
  initialPlaceholderSize: 8,
  dynamicHydrationPlaceholderSize: 8
};
export { createGridWithPod };
